import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useAppData } from "../contexts/AppDataContext";
import "./ShareLanding.css";

/* ── أيقونات المتاجر (SVG inline) ── */
function PlayStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
      <path d="M3.18 23.76c.33.19.7.24 1.06.14L14.88 12 4.24.1C3.88 0 3.51.05 3.18.24 2.5.64 2.06 1.37 2.06 2.2v19.6c0 .83.44 1.56 1.12 1.96zM16.61 13.4l2.72-2.72-11.1-6.4 8.38 9.12zm3.21-3.2 2.36 1.36c.67.39.67 1.29 0 1.68l-2.36 1.36-3.05-2.2 3.05-2.2zM8.23 3.28l11.1 6.4-2.72 2.72L8.23 3.28z"/>
    </svg>
  );
}

function AppStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98l-.09.06c-.22.15-2.18 1.27-2.16 3.8.03 3.02 2.65 4.03 2.68 4.04l-.07.28zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

/* ── خريطة الأنواع ── */
function detectType(pathname) {
  if (pathname.includes("/specialist/")) return "specialist";
  if (pathname.includes("/scale/"))      return "scale";
  if (pathname.includes("/article/"))   return "article";
  if (pathname.includes("/meeting/"))   return "meeting";
  return "package";
}

const TYPE_META = {
  specialist: {
    icon: "👨‍⚕️",
    defaultTitle: "احجز مع الأخصائي من تطبيق تحرر",
    color: "#0aa58c",
  },
  package: {
    icon: "📦",
    defaultTitle: "اكتشف البرنامج في تطبيق تحرر",
    color: "#6c63ff",
  },
  scale: {
    icon: "📊",
    defaultTitle: "جرّب المقياس في تطبيق تحرر",
    color: "#f59e0b",
  },
  article: {
    icon: "📰",
    defaultTitle: "اقرأ المقال في تطبيق تحرر",
    color: "#ef4444",
  },
  meeting: {
    icon: "🎤",
    defaultTitle: "انضم للقاء في تطبيق تحرر",
    color: "#8b5cf6",
  },
};

export default function ShareLanding() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const type = detectType(pathname);
  const meta = TYPE_META[type];

  const { appData, loading: appDataLoading } = useAppData();
  const [title, setTitle]   = useState("");
  const [image, setImage]   = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }

    const endpoints = {
      package:    `/api/packages/${id}`,
      scale:      `/api/scales/public/${id}`,
      article:    `/api/articles/${id}`,
      meeting:    `/api/meetings/public/${id}`,
      specialist: null,
    };

    const url = endpoints[type];
    if (!url) { setLoading(false); return; }

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const d = data?.data;
        if (type === "package")  { setTitle(d?.title || ""); setImage(d?.image || ""); }
        if (type === "scale")    { setTitle(d?.scale?.name || d?.name || ""); setImage(d?.scale?.image || d?.image || ""); }
        if (type === "article")  { setTitle(d?.title || ""); setImage(d?.image || ""); }
        if (type === "meeting")  { setTitle(d?.title || ""); setImage(d?.thumbnail || ""); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, type]);

  const openInApp = () => {
    window.location.href = window.location.href;
  };

  const displayTitle = loading
    ? "جاري التحميل..."
    : title
    ? (type === "package"  ? `اكتشف برنامج: ${title}`
     : type === "scale"    ? `جرّب مقياس: ${title}`
     : type === "article"  ? title
     : type === "meeting"  ? `انضم للقاء: ${title}`
     : meta.defaultTitle)
    : meta.defaultTitle;

  return (
    <div className="sl-page" dir="rtl">
      {/* خلفية بلون النوع */}
      <div className="sl-bg" style={{ "--accent": meta.color }} />

      <div className="sl-card">
        {/* صورة المحتوى (للمقال والبرنامج والمقياس) */}
        {image && !loading && (
          <div className="sl-cover">
            <img src={image} alt={title} />
          </div>
        )}

        {/* أيقونة الافتراضية إذا لم توجد صورة */}
        {!image && !loading && (
          <div className="sl-icon" style={{ background: meta.color + "20", color: meta.color }}>
            {meta.icon}
          </div>
        )}

        {/* الشعار */}
        <div className="sl-brand">
          <img src="/assets/logo.png" alt="تحرر" className="sl-logo" />
          <span className="sl-app-name">تحرر</span>
        </div>

        {/* العنوان */}
        <h1 className="sl-title">{displayTitle}</h1>

        <p className="sl-lead">افتح التطبيق للاستمتاع بكامل التجربة</p>

        {/* الأزرار */}
        <div className="sl-actions">
          <button className="sl-btn sl-btn--open" onClick={openInApp} style={{ background: meta.color }}>
            <span>فتح في التطبيق</span>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {appDataLoading ? (
            <p className="sl-loading-text">جارٍ التحميل…</p>
          ) : (
            <div className="sl-stores">
              {appData?.androidAppLink && (
                <a
                  href={appData.androidAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sl-store-btn sl-store-btn--android"
                >
                  <PlayStoreIcon />
                  <span>
                    <small>تحميل من</small>
                    Google Play
                  </span>
                </a>
              )}
              {appData?.iosAppLink && (
                <a
                  href={appData.iosAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sl-store-btn sl-store-btn--ios"
                >
                  <AppStoreIcon />
                  <span>
                    <small>تحميل من</small>
                    App Store
                  </span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
